"use client"

import { useEffect, useState } from "react"
import { Brain, TrendingUp, TrendingDown, Clock, Newspaper, Loader2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  type PairNewsResponse,
  type RunCycleResponse,
  type TransparencyRecord,
  fetchPairNews,
} from "@/lib/agent-force"

interface CurrencyPairAnalysisProps {
  selectedPair: string | null
  backendAnalysis?: Record<string, RunCycleResponse>
  transparencyRecords?: TransparencyRecord[]
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {}
}

function prettyLabel(raw: string): string {
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatTelemetryValue(value: unknown): string {
  if (typeof value === "number") return Number.isFinite(value) ? value.toString() : "n/a"
  if (typeof value === "boolean") return value ? "true" : "false"
  if (value == null) return "n/a"
  if (Array.isArray(value)) return value.map((x) => String(x)).join(", ")
  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch {
      return "[object]"
    }
  }
  return String(value)
}

function formatScore(value: unknown, digits: number = 3): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return "n/a"
  return n.toFixed(digits)
}

function formatPair(symbol?: string) {
  if (!symbol) return "N/A"
  const s = String(symbol).replace(/[^A-Za-z]/g, "").toUpperCase()
  if (s.length >= 6) return `${s.slice(0, 3)}/${s.slice(3, 6)}`
  return symbol
}

function normalizeLogicalSymbol(value: string): string {
  const s = String(value || "").replace(/[^A-Za-z]/g, "").toUpperCase()
  return s.length >= 6 ? s.slice(0, 6) : s
}

function dedupeHeadlines(lines: (string | undefined)[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const h of lines) {
    const t = String(h ?? "").trim()
    if (!t || seen.has(t)) continue
    seen.add(t)
    out.push(t)
  }
  return out
}

/**
 * Only titles from the market agent's relevance pass (`headlines` on the API).
 * We do not use `fetched_headlines` (raw feed) here — those are not pair-filtered.
 */
function relevantHeadlinesForPair(
  market: Record<string, unknown>,
  pairNews: PairNewsResponse | null,
): string[] {
  return dedupeHeadlines([
    ...(Array.isArray(market.headlines) ? (market.headlines as string[]) : []),
    ...(Array.isArray(pairNews?.headlines) ? (pairNews.headlines as string[]) : []),
  ])
}

/** Match market overview / agent fleet scroll height on desktop. */
const ANALYSIS_SCROLL_CLASS = "h-[min(420px,50vh)]"
const EMPTY_STATE_CLASS = "flex flex-col items-center justify-center min-h-[min(280px,40vh)] py-8 text-center"

export function CurrencyPairAnalysis({ selectedPair, backendAnalysis, transparencyRecords }: CurrencyPairAnalysisProps) {
  const [pairNews, setPairNews] = useState<PairNewsResponse | null>(null)
  const [pairNewsLoading, setPairNewsLoading] = useState(false)
  const [pairNewsError, setPairNewsError] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedPair) {
      setPairNews(null)
      setPairNewsError(null)
      setPairNewsLoading(false)
      return
    }
    const ac = new AbortController()
    setPairNewsLoading(true)
    setPairNewsError(null)
    void fetchPairNews(selectedPair)
      .then((data) => {
        if (ac.signal.aborted) return
        setPairNews(data)
        setPairNewsError(null)
      })
      .catch((err) => {
        if (ac.signal.aborted) return
        setPairNews(null)
        setPairNewsError(err instanceof Error ? err.message : "Failed to load news")
      })
      .finally(() => {
        if (!ac.signal.aborted) setPairNewsLoading(false)
      })
    return () => ac.abort()
  }, [selectedPair])

  if (!selectedPair) {
    return (
      <Card className="min-w-0 w-full bg-card border-border lg:col-span-2 self-start">
        <CardContent className={EMPTY_STATE_CLASS}>
          <div className="w-14 h-14 rounded-full bg-secondary/50 flex items-center justify-center mb-3">
            <Brain className="h-7 w-7 text-muted-foreground" />
          </div>
          <h3 className="text-base font-semibold mb-1.5">Select a Currency Pair</h3>
          <p className="text-muted-foreground text-sm max-w-sm">
            Click any pair in Market Overview for agent analysis, predictions, and thinking.
          </p>
        </CardContent>
      </Card>
    )
  }

  const symbolKey = normalizeLogicalSymbol(selectedPair)
  const candidatePairKeys = [selectedPair, `${symbolKey.slice(0, 3)}/${symbolKey.slice(3)}`, symbolKey].filter(
    Boolean,
  ) as string[]
  const live =
    candidatePairKeys.map((k) => backendAnalysis?.[k]).find((x) => x != null) ?? null
  const record =
    (transparencyRecords || []).find(
      (item) =>
        normalizeLogicalSymbol(String(item.requested_symbol || "")) === symbolKey ||
        normalizeLogicalSymbol(String(item.resolved_symbol || "")) === symbolKey,
    ) || null
  const runOutput = asObject(record?.run_cycle_output ?? live ?? {})
  const symbolEvaluations = Array.isArray(runOutput.symbol_evaluations)
    ? (runOutput.symbol_evaluations as Array<Record<string, unknown>>)
    : []
  const selectedEvaluation =
    symbolEvaluations.find((ev) => {
      const requested = normalizeLogicalSymbol(String(ev.requested_symbol || ""))
      const resolved = normalizeLogicalSymbol(String(ev.resolved_symbol || ""))
      return requested === symbolKey || resolved === symbolKey
    }) ?? null

  const marketRecord = asObject(record?.market ?? {})
  const headRelevant = relevantHeadlinesForPair(marketRecord, pairNews)
  const hasHeadlines = headRelevant.length > 0
  const pairLabel = formatPair(
    (record?.resolved_symbol as string) || (pairNews?.pair as string) || (live?.resolved_symbol as string) || symbolKey,
  )
  const newsScore = Number(marketRecord.news_score ?? pairNews?.news_score ?? live?.market_context?.news_score ?? 0)
  const newsSummary = String(marketRecord.llm_analysis ?? pairNews?.llm_analysis ?? "").trim()
  const showFullAgents = record != null || live != null

  const newsBlock = (
    <div className="rounded-lg border p-4 bg-sky-500/5 border-sky-500/20">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h4 className="font-semibold flex items-center gap-2">
          <Newspaper className="h-4 w-4" />
          Relevant headlines
        </h4>
        {pairNewsLoading && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Updating
          </span>
        )}
      </div>
      {pairNewsError && (
        <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">{pairNewsError}</p>
      )}
      {hasHeadlines && (
        <p className="text-xs text-muted-foreground mb-2">Scored for this pair (market agent relevance pass, not the full news wire).</p>
      )}
      {!hasHeadlines && !pairNewsLoading && !pairNewsError && (
        <p className="text-sm text-muted-foreground">
          No relevant headlines for this pair yet. Check NewsAPI/Investing on the server, or wait for the next news ingest.
        </p>
      )}
      {headRelevant.length > 0 && (
        <ul className="list-disc pl-4 space-y-1.5 text-sm">
          {headRelevant.map((h, i) => (
            <li key={`r-${i}`} className="leading-snug text-foreground/90">
              {h}
            </li>
          ))}
        </ul>
      )}
      {newsSummary && !showFullAgents ? (
        <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border/60">{newsSummary}</p>
      ) : null}
    </div>
  )

  if (!showFullAgents) {
    return (
      <Card className="min-w-0 w-full bg-card border-border lg:col-span-2 self-start">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-xl font-bold">{pairLabel}</CardTitle>
            <Badge
              variant="outline"
              className={`font-mono ${
                newsScore >= 0
                  ? "text-positive border-positive/30 bg-positive/10"
                  : "text-negative border-negative/30 bg-negative/10"
              }`}
            >
              {newsScore >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              News score {newsScore}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Technical, market, and portfolio detail below appear after a run for this symbol is in the dashboard feed. Relevant headlines load
            live from the market agent (pair-scored only).
          </p>
        </CardHeader>
        <CardContent>
          <ScrollArea className={`${ANALYSIS_SCROLL_CLASS} pr-4`}>
            <div className="rounded-lg border border-dashed border-border/80 p-4 mb-4 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">No synced agent run for this pair</p>
              <p>
                When the orchestration cycle includes {pairLabel}, you will see Technical, Market, and Portfolio sections here. Trigger a
                run from the app or autotrade so this card matches the main analysis pipeline.
              </p>
            </div>
            {newsBlock}
          </ScrollArea>
        </CardContent>
      </Card>
    )
  }

  const technical = asObject(record?.technical ?? {})
  const portfolioAgent = asObject(record?.portfolio_agent ?? {})
  const proposal = asObject(live?.proposal ?? {})
  const rejectTelemetry = asObject(technical.reject_telemetry)
  const rejectReasonCode = String(rejectTelemetry.reason_code ?? technical.signal_state ?? "").trim()
  const passedGates = Array.isArray(rejectTelemetry.passed_gates)
    ? rejectTelemetry.passed_gates.map((x) => String(x)).filter(Boolean)
    : []
  const rejectMetrics = asObject(rejectTelemetry.metrics)
  const rejectExtra = asObject(rejectTelemetry.extra)
  const rejectMetricEntries = Object.entries(rejectMetrics).filter(([, v]) => v !== null && v !== undefined && String(v) !== "")
  const rejectExtraEntries = Object.entries(rejectExtra).filter(([, v]) => v !== null && v !== undefined && String(v) !== "")
  const hasRejectTelemetry =
    rejectReasonCode.length > 0 || passedGates.length > 0 || rejectMetricEntries.length > 0 || rejectExtraEntries.length > 0
  const portfolioAnalysis = asObject(portfolioAgent.portfolio_analysis)
  const indicators = asObject(technical.indicators)
  const keyDrivers = Array.isArray(marketRecord.llm_key_drivers) ? marketRecord.llm_key_drivers : []
  const riskFlags = Array.isArray(marketRecord.llm_risk_flags) ? marketRecord.llm_risk_flags : []
  const entry = Number(proposal.entry ?? 0)
  const priceText = entry ? entry.toFixed(pairLabel.includes("JPY") ? 2 : 4) : "N/A"
  const qualityComponents = asObject(selectedEvaluation?.trade_quality_components)
  const qualityComponentEntries = Object.entries(qualityComponents).filter(([, v]) => Number.isFinite(Number(v)))
  const memoryFeedback = asObject(selectedEvaluation?.memory_feedback)
  const memoryOutcome = Array.isArray(memoryFeedback.last_5_outcomes)
    ? (memoryFeedback.last_5_outcomes as unknown[]).map((x) => Number(x)).filter((x) => Number.isFinite(x))
    : []
  const qualityStatus = String(selectedEvaluation?.status ?? "n/a")
  const qualityRejectReason = String(selectedEvaluation?.rejection_reason ?? "").trim()
  const qualityRejectDetail = String(selectedEvaluation?.rejection_detail ?? "").trim()

  return (
    <Card className="min-w-0 w-full bg-card border-border lg:col-span-2 self-start">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle className="text-xl font-bold">{pairLabel}</CardTitle>
            <div className="flex items-center gap-2">
              <span className="font-mono text-2xl font-semibold">{priceText}</span>
              <Badge
                variant="outline"
                className={`font-mono ${
                  newsScore >= 0 ? "text-positive border-positive/30 bg-positive/10" : "text-negative border-negative/30 bg-negative/10"
                }`}
              >
                {newsScore >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                News score {newsScore}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            Live Analysis
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className={`${ANALYSIS_SCROLL_CLASS} pr-4`}>
          <div className="space-y-6">
            {newsBlock}

            <div className="rounded-lg border p-4 bg-blue-500/5 border-blue-500/20">
              <h4 className="font-semibold mb-2">Technical Agent</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Direction: {String(technical.direction ?? proposal.direction ?? "n/a").toUpperCase()} | Confidence:{" "}
                {Number(technical.confidence ?? proposal.confidence ?? 0).toFixed(2)}
              </p>
              <p className="text-sm mb-2">{String(technical.explanation ?? technical.reasoning ?? proposal.reasoning ?? "No technical explanation.")}</p>
              <p className="text-xs text-muted-foreground">
                RSI {String(indicators.rsi ?? "n/a")} | MACD {String(indicators.macd ?? "n/a")} | EMA50 {String(indicators.ema_50 ?? "n/a")} | EMA200{" "}
                {String(indicators.ema_200 ?? "n/a")}
              </p>
              {hasRejectTelemetry && (
                <div className="mt-3 rounded-md border border-amber-500/25 bg-amber-500/5 p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Reject Telemetry</p>
                    {rejectReasonCode ? (
                      <Badge variant="outline" className="text-[11px] border-amber-500/40 text-amber-700 dark:text-amber-300">
                        {rejectReasonCode}
                      </Badge>
                    ) : null}
                  </div>

                  {passedGates.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Passed gates</p>
                      <div className="flex flex-wrap gap-1.5">
                        {passedGates.map((gate) => (
                          <Badge key={gate} variant="secondary" className="text-[11px]">
                            {prettyLabel(gate)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {rejectMetricEntries.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Metrics</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        {rejectMetricEntries.map(([key, value]) => (
                          <p key={key} className="text-muted-foreground">
                            <span className="font-mono text-foreground">{key}</span>: {formatTelemetryValue(value)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {rejectExtraEntries.length > 0 && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Details</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        {rejectExtraEntries.map(([key, value]) => (
                          <p key={key} className="text-muted-foreground">
                            <span className="font-mono text-foreground">{key}</span>: {formatTelemetryValue(value)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-lg border p-4 bg-violet-500/5 border-violet-500/20">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <h4 className="font-semibold">Trade Quality & Memory Feedback</h4>
                <Badge variant="outline" className="font-mono">
                  status {qualityStatus}
                </Badge>
              </div>
              {selectedEvaluation ? (
                <>
                  <p className="text-sm text-muted-foreground mb-2">
                    TQS {formatScore(selectedEvaluation.trade_quality_score)} / 0.750 gate | Combined{" "}
                    {formatScore(selectedEvaluation.combined_score)} (floor {formatScore(selectedEvaluation.combined_score_floor)})
                  </p>
                  {qualityRejectReason && (
                    <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                      Rejection: <span className="font-mono">{qualityRejectReason}</span>
                      {qualityRejectDetail ? ` — ${qualityRejectDetail}` : ""}
                    </p>
                  )}
                  {qualityComponentEntries.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs mb-3">
                      {qualityComponentEntries.map(([k, v]) => (
                        <p key={k} className="text-muted-foreground">
                          <span className="font-mono text-foreground">{k}</span>: {formatScore(v)}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>
                      Pattern WR {formatScore(memoryFeedback.pattern_win_rate, 2)} ({String(memoryFeedback.pattern_sample ?? "0")} samples) | Last 10
                      net PnL {formatScore(memoryFeedback.last_10_net_pnl, 2)} | Risk mult {formatScore(memoryFeedback.risk_multiplier, 2)}
                    </p>
                    <p>
                      Confidence delta {formatScore(memoryFeedback.confidence_delta, 2)} | Same-setup recent losses{" "}
                      {String(memoryFeedback.same_setup_recent_losses ?? "0")}
                      {memoryFeedback.pause_symbol ? ` | PAUSED (${String(memoryFeedback.pause_reason ?? "memory_gate")})` : ""}
                    </p>
                    {memoryOutcome.length > 0 && (
                      <p>
                        Last 5 outcomes:{" "}
                        <span className="font-mono">
                          {memoryOutcome.map((x) => (x > 0 ? "+1" : x < 0 ? "-1" : "0")).join(", ")}
                        </span>
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No symbol evaluation payload was found for this pair yet in the latest cycle output.
                </p>
              )}
            </div>

            <div className="rounded-lg border p-4 bg-emerald-500/5 border-emerald-500/20">
              <h4 className="font-semibold mb-2">Market Agent (Articles + AI Summary)</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Signal {String(marketRecord.market_signal ?? "neutral")} | Confidence {String(marketRecord.market_confidence ?? "n/a")} | Articles{" "}
                {String(marketRecord.article_count ?? "0")} (relevant {String(marketRecord.relevant_articles ?? "0")})
              </p>
              <p className="text-sm mb-2">{String(marketRecord.llm_analysis ?? "No AI market summary returned.")}</p>
              {keyDrivers.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">Drivers: {keyDrivers.map((h) => String(h)).join(" | ")}</p>
              )}
              {riskFlags.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">Risk flags: {riskFlags.map((h) => String(h)).join(" | ")}</p>
              )}
            </div>

            <div className="rounded-lg border p-4 bg-amber-500/5 border-amber-500/20">
              <h4 className="font-semibold mb-2">Portfolio Agent Analysis</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Growth objective: {String(portfolioAnalysis.growth_objective ?? "n/a")} | Same symbol count:{" "}
                {String(portfolioAnalysis.same_symbol_count ?? "n/a")}
              </p>
              <p className="text-sm">
                {String(asObject(portfolioAnalysis.llm_suggestion).rationale ?? "No portfolio-agent rationale returned.")}
              </p>
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
